import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Membership() {
  const [tiers, setTiers] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/tiers").then((r) => {
      setTiers(r.data.tiers);
      setComparison(r.data.comparison);
    }).catch(() => {});
  }, []);

  const signup = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/memberships", { tier_id: selected.id, name, email });
      setConfirmation(r.data);
    } catch {
      toast.error("Signup failed — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setSelected(null);
    setConfirmation(null);
    setName("");
    setEmail("");
  };

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="membership-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <p className="text-[#ccff00] uppercase tracking-[0.3em] text-sm mb-4">Membership</p>
        <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">
          Pick your <span className="text-[#ccff00]">game plan.</span>
        </h1>
        <p className="text-white/60 max-w-xl mt-6 leading-relaxed">
          Three tiers, no contracts, all simulated. Sign up for a mock membership and get an instant confirmation.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
        {tiers.map((t, i) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.5 }}
            className={`rounded-md p-8 flex flex-col ${
              t.highlight
                ? "border border-[#ccff00]/60 shadow-[0_0_25px_rgba(204,255,0,0.18)] bg-[#0f0f10] md:-translate-y-3"
                : "border border-[#2a2a2a] bg-[#0f0f10]"
            }`}
            data-testid={`tier-card-${t.id}`}
          >
            <span className={`self-start text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full ${t.highlight ? "bg-[#ccff00] text-black font-bold" : "border border-white/20 text-white/60"}`}>
              {t.tag}
            </span>
            <h2 className="font-display uppercase text-3xl tracking-tight mt-6">{t.name}</h2>
            <p className="mt-4">
              <span className="font-display text-5xl text-[#ccff00]">${t.price}</span>
              <span className="text-white/50 text-sm"> / month</span>
            </p>
            <ul className="mt-8 space-y-3 flex-1">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-white/75 text-sm">
                  <Check size={16} className="text-[#ccff00] mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => setSelected(t)}
              data-testid={`tier-join-btn-${t.id}`}
              className={`mt-10 w-full font-bold uppercase tracking-wider py-3.5 rounded-full transition-all duration-200 ${
                t.highlight
                  ? "bg-[#ccff00] text-black hover:bg-[#a3e635] hover:-translate-y-0.5"
                  : "border border-white/25 text-white hover:border-[#ccff00]/70 hover:text-[#ccff00]"
              }`}
            >
              Join {t.name}
            </button>
          </motion.div>
        ))}
      </div>

      {/* Comparison table */}
      <section className="mt-24" data-testid="comparison-table-section">
        <h2 className="font-display uppercase text-3xl lg:text-4xl tracking-tight mb-10">Compare tiers</h2>
        <div className="overflow-x-auto border border-[#2a2a2a] rounded-md">
          <table className="w-full text-left border-collapse min-w-[640px]" data-testid="comparison-table">
            <thead>
              <tr className="border-b border-[#2a2a2a] bg-[#0f0f10]">
                <th className="px-6 py-5 text-white/50 uppercase tracking-widest text-xs font-normal">Feature</th>
                <th className="px-6 py-5 font-display uppercase tracking-tight text-lg">Baseline</th>
                <th className="px-6 py-5 font-display uppercase tracking-tight text-lg text-[#ccff00]">Ace</th>
                <th className="px-6 py-5 font-display uppercase tracking-tight text-lg">Grand Slam</th>
              </tr>
            </thead>
            <tbody>
              {comparison.map((row) => (
                <tr key={row.feature} className="border-b border-[#2a2a2a]/60 hover:bg-white/[0.03] transition-colors">
                  <td className="px-6 py-4 text-white/70 text-sm">{row.feature}</td>
                  <td className="px-6 py-4 text-sm">{row.baseline}</td>
                  <td className="px-6 py-4 text-sm text-[#ccff00]">{row.ace}</td>
                  <td className="px-6 py-4 text-sm">{row.grand_slam}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-[#0f0f10] border-white/15 text-white max-w-md max-h-[90vh] [max-height:90dvh] overflow-y-auto" data-testid="membership-dialog">
          {confirmation ? (
            <div className="text-center py-6" data-testid="membership-confirmation">
              <CheckCircle2 className="mx-auto text-[#ccff00] mb-4" size={56} />
              <h3 className="font-display uppercase text-3xl tracking-tight mb-2">Welcome to the club</h3>
              <p className="text-white/60 mb-6">Mock membership — nothing was charged.</p>
              <div className="bg-[#1a1a1a] border border-[#ccff00]/30 rounded-lg p-6 text-left space-y-2">
                <p className="text-[#ccff00] font-mono text-lg" data-testid="membership-ref">{confirmation.ref}</p>
                <p><span className="text-white/50">Tier:</span> {confirmation.tier_name} — ${confirmation.price}/mo</p>
                <p><span className="text-white/50">Member:</span> {confirmation.name}</p>
              </div>
              <button onClick={close} data-testid="membership-done-btn" className="mt-6 bg-[#ccff00] text-black font-bold uppercase tracking-wider px-8 py-3 rounded-full hover:bg-[#a3e635] transition-colors">
                Done
              </button>
            </div>
          ) : (
            selected && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display uppercase text-2xl tracking-tight">
                    Join <span className="text-[#ccff00]">{selected.name}</span> — ${selected.price}/mo
                  </DialogTitle>
                  <DialogDescription className="text-white/50">Mock signup — instant confirmation, nothing charged.</DialogDescription>
                </DialogHeader>
                <form onSubmit={signup} className="space-y-4 mt-2">
                  <input
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    data-testid="membership-name-input"
                    aria-label="Your name"
                    className="w-full bg-[#1a1a1a] border border-white/15 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#ccff00]/60 placeholder:text-white/35"
                  />
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    data-testid="membership-email-input"
                    aria-label="Email"
                    className="w-full bg-[#1a1a1a] border border-white/15 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#ccff00]/60 placeholder:text-white/35"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="membership-submit-btn"
                    className="w-full bg-[#ccff00] text-black font-bold uppercase tracking-wider py-3.5 rounded-full hover:bg-[#a3e635] transition-colors disabled:opacity-50"
                  >
                    {submitting ? "Joining…" : "Confirm mock signup"}
                  </button>
                  <p className="text-white/40 text-xs text-center">Demo only — no payment, no commitment.</p>
                </form>
              </>
            )
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
